import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import './Sanctuary.css';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

function Sanctuary({ user, token }) {
  const navigate = useNavigate();
  const [videos, setVideos] = useState([]);
  const [selectedVideo, setSelectedVideo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showControls, setShowControls] = useState(true);

  // Fetch healing frequency videos
  useEffect(() => {
    const fetchVideos = async () => {
      try {
        const response = await fetch(`${API_URL}/api/sanctuary/frequencies`);
        if (response.ok) {
          const data = await response.json();
          setVideos(data.videos || []);
        } else {
          setError('Failed to load healing frequencies');
        }
      } catch (err) {
        console.error('Error fetching frequencies:', err);
        setError('Failed to connect to server');
      } finally {
        setLoading(false);
      }
    };
    
    fetchVideos();
  }, []);

  // Auto-hide controls after inactivity
  useEffect(() => {
    let timeout;
    const handleMouseMove = () => {
      setShowControls(true);
      clearTimeout(timeout);
      timeout = setTimeout(() => {
        if (!selectedVideo) setShowControls(false);
      }, 5000);
    };
    
    window.addEventListener('mousemove', handleMouseMove);
    timeout = setTimeout(() => {
      if (!selectedVideo) setShowControls(false);
    }, 5000);
    
    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      clearTimeout(timeout);
    };
  }, [selectedVideo]);

  const selectVideo = (video) => {
    setSelectedVideo(video);
    setShowControls(true);
  };

  const closeVideo = () => {
    setSelectedVideo(null);
  };

  return (
    <div className="sanctuary-page" data-testid="sanctuary-page">
      {/* Background image */}
      <div className="sanctuary-background">
        <img 
          src="https://customer-assets.emergentagent.com/job_page-builder-225/artifacts/3qhaag9q_Sanctuary.png"
          alt="The Sanctuary"
          className="sanctuary-bg-image"
        />
        <div className="sanctuary-overlay"></div>
      </div>

      {/* Header - Back button */}
      <header className={`sanctuary-header ${showControls ? 'visible' : 'hidden'}`}>
        <button 
          className="sanctuary-back-btn"
          onClick={() => navigate('/guild-hall')}
          data-testid="back-to-guild"
        >
          <span className="back-arrow">←</span>
          <span className="back-text">Return to Guild Hall</span>
        </button>
        
        <div className="sanctuary-title-area">
          <h1 className="sanctuary-title">THE SANCTUARY</h1>
          <p className="sanctuary-subtitle">Healing Frequencies for Mind, Body & Soul</p>
        </div>
        
        <div className="header-spacer"></div>
      </header>

      {/* Video Player Modal */}
      {selectedVideo && (
        <div className="video-player-overlay" onClick={closeVideo}>
          <div className="video-player-container" onClick={e => e.stopPropagation()}>
            <button className="video-close-btn" onClick={closeVideo}>×</button>
            <div className="video-wrapper">
              <iframe
                src={`https://www.youtube.com/embed/${selectedVideo.id}?autoplay=1&rel=0`}
                title={selectedVideo.title}
                frameBorder="0"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
              ></iframe>
            </div>
            <div className="video-info">
              <h3>{selectedVideo.title}</h3>
              <p>{selectedVideo.description?.substring(0, 200)}{selectedVideo.description?.length > 200 ? '...' : ''}</p>
            </div>
          </div>
        </div>
      )}

      {/* Frequencies Grid */}
      <div className={`sanctuary-frequencies ${showControls ? 'visible' : 'hidden'}`}>
        <div className="frequencies-label">
          <span className="frequency-icon">🎵</span>
          Healing Frequencies
        </div>
        
        {loading ? (
          <div className="frequencies-loading">
            <div className="loading-spinner"></div>
            <p>Loading healing frequencies...</p>
          </div>
        ) : error ? (
          <div className="frequencies-error">
            <p>{error}</p>
            <button onClick={() => window.location.reload()}>Retry</button>
          </div>
        ) : videos.length === 0 ? (
          <div className="frequencies-empty">
            <p>No frequencies available yet. Check back soon!</p>
          </div>
        ) : (
          <div className="frequencies-grid">
            {videos.map(video => (
              <button
                key={video.id}
                className="frequency-card"
                onClick={() => selectVideo(video)}
                data-testid={`frequency-${video.id}`}
              >
                <div className="frequency-thumbnail">
                  <img src={video.thumbnail} alt={video.title} />
                  <div className="play-overlay">
                    <span className="play-icon">▶</span>
                  </div>
                </div>
                <div className="frequency-title">{video.title}</div>
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Quote at bottom */}
      <footer className={`sanctuary-footer ${showControls ? 'visible' : 'hidden'}`}>
        <div className="sanctuary-quote">
          <p className="quote-text">
            "If you want to find the secrets of the universe, think in terms of energy, frequency and vibration."
          </p>
          <p className="quote-author">— Nikola Tesla</p>
        </div>
      </footer>
    </div>
  );
}

export default Sanctuary;
